/**
 * Agent brandmark that appears in the CRM top nav, mobile drawer, and any
 * "who am I" identity block. Priority:
 *   1. Uploaded agent headshot (agent.photo_url) — the person the client
 *      called. Rendered as a circular avatar.
 *   2. Initials disc — gradient using the agent's primary + accent brand
 *      colors so it feels intentional rather than a placeholder.
 *   3. Empty branded square — only when there is no agent context at all
 *      (rare, e.g. a shell rendered on the login redirect before `/auth/me`
 *      resolves).
 *
 * The brokerage logo is deliberately NOT used here — it lives only in the
 * portal footer and on official document previews.
 */
export default function AgentBrandmark({ agent, size = 32, alt }) {
  const px = `${size}px`;
  const photoUrl = agent?.photo_url;
  const fullName = agent?.full_name || "";
  const label = alt || fullName || "Home";

  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt={label}
        className="shrink-0 rounded-full object-cover ring-1 ring-slate-200"
        style={{ height: px, width: px }}
        data-testid="brandmark-photo"
      />
    );
  }

  if (fullName) {
    const initials = fullName.split(" ").filter(Boolean).slice(0, 2)
      .map(s => s[0].toUpperCase()).join("");
    return (
      <div
        className="shrink-0 rounded-full flex items-center justify-center text-white font-heading font-bold tracking-tight ring-1 ring-white/40 shadow-sm"
        style={{
          height: px, width: px,
          fontSize: Math.max(11, Math.round(size * 0.42)),
          background: "linear-gradient(135deg, var(--primary-hex) 0%, var(--accent-hex) 100%)",
        }}
        role="img"
        aria-label={label}
        data-testid="brandmark-initials"
      >
        {initials || "?"}
      </div>
    );
  }

  // Pre-auth fallback — a plain branded circle using the current CSS vars.
  return (
    <div
      className="shrink-0 rounded-full ring-1 ring-white/40 shadow-sm"
      style={{
        height: px, width: px,
        background: "linear-gradient(135deg, var(--primary-hex) 0%, var(--accent-hex) 100%)",
      }}
      aria-label={label}
      data-testid="brandmark-default"
    />
  );
}
